/**
 * skuKey — the ONE canonical SKU join-key normalizer (R51 T2).
 *
 * Trim, collapse internal whitespace to a single space, casefold (lowercase). Used at EVERY kiosk
 * join point (the last-shown card cache + the server-resolved itemMeta lookup) so a SKU emitted by
 * the LLM / tapped by the visitor / stored in D1 joins even when whitespace or case drift. It is
 * JOIN-KEY ONLY: the displayed / emitted / persisted SKU always stays the original string — never
 * normalize what we show or send.
 *
 * MUST stay identical to the Python implementation `agent/tools/catalogue.py :: sku_key`.
 *
 * Why this rule is safe (near-collision check, R51): run over all 2,361 resolved D1 SKUs it produces
 * ZERO collisions — the catalogue is already uppercase + single-spaced, and the only near-miss pair
 * (`"0023 LMS"` vs `"00023 LMS"`) differs by a leading zero, which no whitespace/case rule touches.
 * The disambiguation separator `~` (e.g. `"0031~VASES229"`) and `/` (e.g. `"00028 L/S"`) are
 * preserved verbatim, so distinct internal SKUs stay distinct.
 */
export function skuKey(sku: unknown): string {
  if (typeof sku !== 'string') return '';
  return sku.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Build a Map keyed by the canonical sku_key, preserving the last-wins value semantics of a Map. */
export function bySkuKey<T>(entries: Iterable<[string, T]>): Map<string, T> {
  const out = new Map<string, T>();
  for (const [sku, v] of entries) out.set(skuKey(sku), v);
  return out;
}
